import React, { useEffect, useMemo, useState } from 'react';
import { getErrorMessage } from '../utils/errorHandler';
import HojoSelect from './HojoSelect';

interface ClassicGeneralLedgerProps {
  onMenuSelect: (menuId: string) => void;
  tenantName: string;
  currentUser: any;
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  currentTenantId: number | null;
}

interface KamokuOption {
  id: number;
  kamoku: string;
  kamoku_code?: string;
  taisyaku_kubun?: string;
}

interface LedgerTransaction {
  date: string;
  denpyou_no: string;
  tekiyou: string;
  aite_kamoku: string;
  karikata: number;
  kashikata: number;
  balance: number;
}

interface LedgerAccount {
  kamoku_id: number;
  kamoku_code: string;
  kamoku_name: string;
  taisyaku_kubun: string;
  opening_balance: number;
  closing_balance: number;
  transactions: LedgerTransaction[];
  transaction_count: number;
}

interface LedgerResponse {
  period_start: string;
  period_end: string;
  accounts: LedgerAccount[];
}

const currencyFormatter = new Intl.NumberFormat('ja-JP');

const formatDateInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ClassicGeneralLedger: React.FC<any> = ({
  onMenuSelect,
  tenantName,
  currentUser,
  apiFetch,
  currentTenantId,
}) => {
  const currentYear = useMemo(() => {
    const saved = localStorage.getItem('selectedFiscalYear');
    return saved ? parseInt(saved, 10) : new Date().getFullYear();
  }, []);

  const defaultStart = useMemo(
    () => `${currentYear}-04-01`,
    [currentYear],
  );
  const defaultEnd = useMemo(
    () => `${currentYear + 1}-03-31`,
    [currentYear],
  );

  const [periodStart, setPeriodStart] = useState(defaultStart);
  const [periodEnd, setPeriodEnd] = useState(defaultEnd);
  const [kamokuOptions, setKamokuOptions] = useState<KamokuOption[]>([]);
  const [selectedKamokuId, setSelectedKamokuId] = useState<string>('');
  const [selectedHojoId, setSelectedHojoId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ledger, setLedger] = useState<LedgerResponse | null>(null);
  const [isFetchingKamoku, setIsFetchingKamoku] = useState(false);

  const username =
    typeof currentUser === 'string' ? currentUser : currentUser?.username || 'admin';

  const fetchKamokuOptions = async () => {
    setIsFetchingKamoku(true);
    try {
      const response = await apiFetch('/api/kamoku');
      if (!response.ok) {
        const errorMessage = await getErrorMessage(response, '勘定科目の取得に失敗しました');
        throw new Error(errorMessage);
      }
      const data = await response.json();
      setKamokuOptions(data || []);
    } catch (err: any) {
      console.error('勘定科目の取得に失敗しました', err);
      setError(err?.message || '勘定科目の取得に失敗しました');
    } finally {
      setIsFetchingKamoku(false);
    }
  };

  const fetchLedger = async (params?: { start?: string; end?: string; kamokuId?: string; hojoId?: string }) => {
    if (!periodStart || !periodEnd) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const searchParams = new URLSearchParams({
        period_start: params?.start ?? periodStart,
        period_end: params?.end ?? periodEnd,
      });
      const kamokuIdToUse = params?.kamokuId ?? selectedKamokuId;
      if (kamokuIdToUse) {
        searchParams.append('kamoku_id', kamokuIdToUse);
      }
      const hojoIdToUse = params?.hojoId ?? selectedHojoId;
      if (hojoIdToUse) {
        searchParams.append('hojo_id', hojoIdToUse);
      }
      const response = await apiFetch(`/api/general-ledger?${searchParams.toString()}`);
      if (!response.ok) {
        const errorMessage = await getErrorMessage(response, '総勘定元帳の取得に失敗しました');
        throw new Error(errorMessage);
      }
      const data = (await response.json()) as LedgerResponse;
      setLedger(data);
    } catch (err: any) {
      console.error('総勘定元帳の取得に失敗しました', err);
      setError(err?.message || '総勘定元帳の取得に失敗しました');
      setLedger(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKamokuOptions();
  }, [apiFetch, currentTenantId]);

  useEffect(() => {
    fetchLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenantId]);

  const handleSearch = () => {
    fetchLedger({ start: periodStart, end: periodEnd, kamokuId: selectedKamokuId, hojoId: selectedHojoId });
  };

  const handleReset = () => {
    setPeriodStart(defaultStart);
    setPeriodEnd(defaultEnd);
    setSelectedKamokuId('');
    setSelectedHojoId('');
    fetchLedger({ start: defaultStart, end: defaultEnd, kamokuId: '', hojoId: '' });
  };

  const renderAccountTable = (account: LedgerAccount) => {
    const rows = account.transactions;
    return (
      <fieldset
        key={account.kamoku_id}
        style={{
          border: '2px inset #d0d0d0',
          backgroundColor: '#ffffff',
          marginBottom: '20px',
        }}
      >
        <legend
          style={{
            fontWeight: 'bold',
            fontSize: '12px',
            padding: '0 6px',
          }}
        >
          {account.kamoku_code} {account.kamoku_name}
        </legend>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '11px',
            marginBottom: '8px',
            padding: '0 8px',
          }}
        >
          <span>
            期首残高:{' '}
            <strong>¥{currencyFormatter.format(account.opening_balance)}</strong>
          </span>
          <span>
            期末残高:{' '}
            <strong>¥{currencyFormatter.format(account.closing_balance)}</strong>
          </span>
        </div>
        <div
          style={{
            border: '1px solid #c0c0c0',
            overflowX: 'auto',
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '11px',
            }}
          >
            <thead>
              <tr style={{ backgroundColor: '#e0e0e0' }}>
                <th
                  style={{
                    padding: '6px',
                    borderBottom: '2px solid #808080',
                    textAlign: 'left',
                    width: '90px',
                  }}
                >
                  日付
                </th>
                <th
                  style={{
                    padding: '6px',
                    borderBottom: '2px solid #808080',
                    textAlign: 'left',
                    width: '110px',
                  }}
                >
                  伝票番号
                </th>
                <th
                  style={{
                    padding: '6px',
                    borderBottom: '2px solid #808080',
                    textAlign: 'left',
                    width: '220px',
                  }}
                >
                  摘要
                </th>
                <th
                  style={{
                    padding: '6px',
                    borderBottom: '2px solid #808080',
                    textAlign: 'left',
                    width: '180px',
                  }}
                >
                  相手科目
                </th>
                <th
                  style={{
                    padding: '6px',
                    borderBottom: '2px solid #808080',
                    textAlign: 'right',
                    width: '120px',
                  }}
                >
                  借方
                </th>
                <th
                  style={{
                    padding: '6px',
                    borderBottom: '2px solid #808080',
                    textAlign: 'right',
                    width: '120px',
                  }}
                >
                  貸方
                </th>
                <th
                  style={{
                    padding: '6px',
                    borderBottom: '2px solid #808080',
                    textAlign: 'right',
                    width: '120px',
                  }}
                >
                  残高
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      padding: '14px',
                      textAlign: 'center',
                      color: '#777',
                    }}
                  >
                    期間内の取引はありません
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr
                    key={`${account.kamoku_id}-${idx}-${row.denpyou_no}`}
                    style={{
                      backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f7f7f7',
                    }}
                  >
                    <td style={{ padding: '6px', borderBottom: '1px solid #e0e0e0' }}>
                      {row.date}
                    </td>
                    <td style={{ padding: '6px', borderBottom: '1px solid #e0e0e0' }}>
                      {row.denpyou_no}
                    </td>
                    <td style={{ padding: '6px', borderBottom: '1px solid #e0e0e0' }}>
                      {row.tekiyou || '（摘要なし）'}
                    </td>
                    <td style={{ padding: '6px', borderBottom: '1px solid #e0e0e0' }}>
                      {row.aite_kamoku || '（相手科目なし）'}
                    </td>
                    <td
                      style={{
                        padding: '6px',
                        borderBottom: '1px solid #e0e0e0',
                        textAlign: 'right',
                      }}
                    >
                      {row.karikata ? `¥${currencyFormatter.format(row.karikata)}` : ''}
                    </td>
                    <td
                      style={{
                        padding: '6px',
                        borderBottom: '1px solid #e0e0e0',
                        textAlign: 'right',
                      }}
                    >
                      {row.kashikata ? `¥${currencyFormatter.format(row.kashikata)}` : ''}
                    </td>
                    <td
                      style={{
                        padding: '6px',
                        borderBottom: '1px solid #e0e0e0',
                        textAlign: 'right',
                      }}
                    >
                      ¥{currencyFormatter.format(row.balance)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </fieldset>
    );
  };

  return (
    <div
      style={{
        backgroundColor: '#f0f0f0',
        minHeight: '100vh',
        fontFamily: "'MS UI Gothic', 'Hiragino Kaku Gothic ProN', sans-serif",
        fontSize: '11px',
      }}
    >
      <div
        style={{
          backgroundColor: '#0066cc',
          color: 'white',
          padding: '10px 18px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>総勘定元帳</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', marginTop: '4px' }}>{tenantName}</div>
        </div>
        <div style={{ textAlign: 'right', fontSize: '10px', lineHeight: 1.4 }}>
          <div>System ver 1.33</div>
          <div>DB ver 3</div>
          <div style={{ marginTop: '4px', color: '#fffb96' }}>ユーザー: {username}</div>
        </div>
      </div>

      <div style={{ padding: '20px' }}>
        <fieldset
          style={{
            border: '2px inset #d0d0d0',
            backgroundColor: '#ffffff',
            marginBottom: '18px',
          }}
        >
          <legend style={{ fontWeight: 'bold', fontSize: '12px', padding: '0 6px' }}>
            抽出条件
          </legend>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px 18px',
              alignItems: 'center',
            }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>期間（自）</span>
              <input
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                style={{ padding: '3px', fontSize: '11px' }}
              />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>期間（至）</span>
              <input
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                style={{ padding: '3px', fontSize: '11px' }}
              />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>勘定科目</span>
              <select
                value={selectedKamokuId}
                onChange={(e) => {
                  setSelectedKamokuId(e.target.value);
                  setSelectedHojoId('');
                }}
                disabled={isFetchingKamoku}
                style={{ padding: '3px', fontSize: '11px', minWidth: '220px' }}
              >
                <option value="">（全ての科目）</option>
                {kamokuOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.kamoku_code ? `${option.kamoku_code} ` : ''}
                    {option.kamoku}
                  </option>
                ))}
              </select>
            </label>
            {selectedKamokuId && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>補助科目</span>
                <HojoSelect
                  kamokuId={parseInt(selectedKamokuId, 10)}
                  value={selectedHojoId ? parseInt(selectedHojoId, 10) : null}
                  onChange={(val) => setSelectedHojoId(val)}
                  apiFetch={apiFetch}
                  currentTenantId={currentTenantId || undefined}
                  hasHojo={true}
                  style={{ width: '180px', height: '23px' }}
                />
              </label>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleSearch}
                disabled={loading}
                style={{
                  padding: '6px 16px',
                  backgroundColor: '#c0c0c0',
                  border: '2px outset #c0c0c0',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '11px',
                  minWidth: '80px',
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.border = '2px inset #c0c0c0';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.border = '2px outset #c0c0c0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.border = '2px outset #c0c0c0';
                }}
              >
                表示
              </button>
              <button
                onClick={handleReset}
                disabled={loading}
                style={{
                  padding: '6px 16px',
                  backgroundColor: '#c0c0c0',
                  border: '2px outset #c0c0c0',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '11px',
                  minWidth: '80px',
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.border = '2px inset #c0c0c0';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.border = '2px outset #c0c0c0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.border = '2px outset #c0c0c0';
                }}
              >
                条件クリア
              </button>
              <button
                onClick={() => alert('PDF出力は現在準備中です。')}
                style={{
                  padding: '6px 16px',
                  backgroundColor: '#e0e0e0',
                  border: '2px outset #e0e0e0',
                  cursor: 'not-allowed',
                  fontSize: '11px',
                  minWidth: '80px',
                }}
              >
                PDF出力
              </button>
            </div>
          </div>
        </fieldset>

        {error && (
          <div
            style={{
              backgroundColor: '#ffe0e0',
              color: '#cc0000',
              border: '1px solid #cc0000',
              padding: '12px',
              marginBottom: '16px',
              fontSize: '11px',
            }}
          >
            エラー: {error}
          </div>
        )}

        {loading ? (
          <div
            style={{
              backgroundColor: '#ffffff',
              border: '1px solid #c0c0c0',
              padding: '24px',
              textAlign: 'center',
              fontSize: '12px',
            }}
          >
            総勘定元帳を読み込んでいます...
          </div>
        ) : ledger && ledger.accounts.length > 0 ? (
          <div>
            <div style={{ fontSize: '11px', marginBottom: '12px', color: '#555' }}>
              表示期間: {ledger.period_start} 〜 {ledger.period_end} / 科目数:{' '}
              {ledger.accounts.length}件
            </div>
            {ledger.accounts.map(renderAccountTable)}
          </div>
        ) : (
          <div
            style={{
              backgroundColor: '#ffffff',
              border: '1px solid #c0c0c0',
              padding: '24px',
              textAlign: 'center',
              fontSize: '12px',
              color: '#777',
            }}
          >
            表示できる総勘定元帳データがありません
          </div>
        )}

        <div style={{ marginTop: '20px' }}>
          <button
            onClick={() => onMenuSelect('main-menu')}
            style={{
              padding: '8px 20px',
              backgroundColor: '#c0c0c0',
              border: '2px outset #c0c0c0',
              cursor: 'pointer',
              fontSize: '11px',
              marginRight: '8px',
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.border = '2px inset #c0c0c0';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.border = '2px outset #c0c0c0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.border = '2px outset #c0c0c0';
            }}
          >
            メインメニューに戻る
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClassicGeneralLedger;
